import { useState, useEffect } from "react";
import { MapPin, EnvelopeSimple, Clock, ArrowRight } from "@phosphor-icons/react";
import axios from "axios";
import SEO, { contactPageSchema, breadcrumbSchema } from "@/components/SEO";
import { API_BASE_URL } from "@/lib/api";

const serviceOptions = [
  "Investing & Trading Advisory",
  "Financial Planning",
  "Tax Planning",
  "Portfolio Management",
  "Security & Custody",
  "Licensing & Compliance",
  "Hiring & Team Building",
  "Blockchain Development",
  "Project Management",
  "Tokenization & Launch Advisory",
  "Other",
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    client_type: "",
    service_interest: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API_BASE_URL}/contact`, form);
      setSubmitted(true);
    } catch (err) {
      setError("Something went wrong. Please try again or email us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <SEO
        title="Contact BitGenix | Book a Free Crypto Consultation"
        description="Get in touch with BitGenix for expert crypto consultancy. Book a free discovery call to discuss your crypto investment, tax planning, compliance, or blockchain development needs. We respond within 24 hours."
        path="/contact"
        keywords={[
          "contact BitGenix",
          "book crypto consultation India",
          "crypto tax consultant contact",
          "FIU-IND consultant contact",
          "blockchain advisory India",
        ]}
        jsonLd={[
          contactPageSchema(),
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Contact", path: "/contact" },
          ]),
        ]}
      />
      {/* Hero */}
      <section className="bg-[#21324D] py-20 sm:py-24">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="max-w-2xl">
            <p className="font-body text-sm uppercase tracking-[0.2em] text-[#A7A55C] mb-6">Contact</p>
            <h1
              className="font-heading font-semibold text-4xl sm:text-5xl text-[#F5F5F2] leading-none tracking-tight mb-6"
              data-testid="contact-heading"
            >
              Let's talk
            </h1>
            <p className="font-body text-lg text-[#F5F5F2]/80 leading-relaxed">
              Tell us about your needs. Our team responds within 24 hours.
            </p>
          </div>
        </div>
      </section>

      {/* Form + Info */}
      <section className="py-24 sm:py-32 bg-[#F5F5F2]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
            {/* Form */}
            <div className="lg:col-span-8">
              {submitted ? (
                <div className="bg-white border border-[#A7A55C]/30 p-12" data-testid="contact-success">
                  <h2 className="font-heading font-semibold text-2xl text-[#21324D] mb-4">
                    Thank you for reaching out
                  </h2>
                  <p className="font-body text-base text-[#383A46] leading-relaxed">
                    We've received your inquiry and will get back to you within 24 hours. If your matter is urgent, you can reach us directly at hello@bitgenix.io
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label className="block font-body text-sm font-medium text-[#21324D] mb-2">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        required
                        className="w-full bg-white border border-[#383A46]/20 px-4 py-3.5 font-body text-sm text-[#21324D] placeholder:text-[#383A46]/40 focus:outline-none focus:border-[#A7A55C] transition-colors"
                        placeholder="Your full name"
                        data-testid="contact-name-input"
                      />
                    </div>
                    <div>
                      <label className="block font-body text-sm font-medium text-[#21324D] mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={form.email}
                        onChange={handleChange}
                        required
                        className="w-full bg-white border border-[#383A46]/20 px-4 py-3.5 font-body text-sm text-[#21324D] placeholder:text-[#383A46]/40 focus:outline-none focus:border-[#A7A55C] transition-colors"
                        placeholder="you@email.com"
                        data-testid="contact-email-input"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label className="block font-body text-sm font-medium text-[#21324D] mb-2">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={form.phone}
                        onChange={handleChange}
                        className="w-full bg-white border border-[#383A46]/20 px-4 py-3.5 font-body text-sm text-[#21324D] placeholder:text-[#383A46]/40 focus:outline-none focus:border-[#A7A55C] transition-colors"
                        placeholder="+91 98765 43210"
                        data-testid="contact-phone-input"
                      />
                    </div>
                    <div>
                      <label className="block font-body text-sm font-medium text-[#21324D] mb-2">
                        Company / Organization
                      </label>
                      <input
                        type="text"
                        name="company"
                        value={form.company}
                        onChange={handleChange}
                        className="w-full bg-white border border-[#383A46]/20 px-4 py-3.5 font-body text-sm text-[#21324D] placeholder:text-[#383A46]/40 focus:outline-none focus:border-[#A7A55C] transition-colors"
                        placeholder="Your company name"
                        data-testid="contact-company-input"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label className="block font-body text-sm font-medium text-[#21324D] mb-2">
                        I am a...
                      </label>
                      <select
                        name="client_type"
                        value={form.client_type}
                        onChange={handleChange}
                        className="w-full bg-white border border-[#383A46]/20 px-4 py-3.5 font-body text-sm text-[#21324D] focus:outline-none focus:border-[#A7A55C] transition-colors"
                        data-testid="contact-type-select"
                      >
                        <option value="">Select</option>
                        <option value="individual">Individual Investor</option>
                        <option value="business">Business / Startup</option>
                        <option value="nri">NRI</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div>
                      <label className="block font-body text-sm font-medium text-[#21324D] mb-2">
                        Service Interest
                      </label>
                      <select
                        name="service_interest"
                        value={form.service_interest}
                        onChange={handleChange}
                        className="w-full bg-white border border-[#383A46]/20 px-4 py-3.5 font-body text-sm text-[#21324D] focus:outline-none focus:border-[#A7A55C] transition-colors"
                        data-testid="contact-service-select"
                      >
                        <option value="">Select a service</option>
                        {serviceOptions.map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block font-body text-sm font-medium text-[#21324D] mb-2">
                      How can we help? *
                    </label>
                    <textarea
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      className="w-full bg-white border border-[#383A46]/20 px-4 py-3.5 font-body text-sm text-[#21324D] placeholder:text-[#383A46]/40 focus:outline-none focus:border-[#A7A55C] transition-colors resize-none"
                      placeholder="Tell us about your situation, goals, or questions..."
                      data-testid="contact-message-input"
                    />
                  </div>
                  {error && (
                    <p className="font-body text-sm text-red-600" data-testid="contact-error">{error}</p>
                  )}
                  <button
                    type="submit"
                    disabled={loading}
                    className="bg-[#A7A55C] text-[#F5F5F2] hover:bg-[#8C8A4A] transition-colors duration-300 font-body font-semibold text-sm tracking-wide px-8 py-4 rounded-none inline-flex items-center gap-2 disabled:opacity-50"
                    data-testid="contact-submit-button"
                  >
                    {loading ? "Sending..." : "Send Message"} <ArrowRight size={16} weight="bold" />
                  </button>
                </form>
              )}
            </div>

            {/* Sidebar Info */}
            <div className="lg:col-span-4">
              <div className="space-y-8">
                <div>
                  <div className="flex items-center gap-3 mb-3">
                    <EnvelopeSimple size={20} weight="duotone" className="text-[#A7A55C]" />
                    <h4 className="font-body font-medium text-sm uppercase tracking-[0.1em] text-[#21324D]">Email</h4>
                  </div>
                  <p className="font-body text-base text-[#383A46]">hello@bitgenix.io</p>
                </div>
                <div>
                  <div className="flex items-center gap-3 mb-3">
                    <MapPin size={20} weight="duotone" className="text-[#A7A55C]" />
                    <h4 className="font-body font-medium text-sm uppercase tracking-[0.1em] text-[#21324D]">Location</h4>
                  </div>
                  <p className="font-body text-base text-[#383A46]">India-first, globally available</p>
                </div>
                <div>
                  <div className="flex items-center gap-3 mb-3">
                    <Clock size={20} weight="duotone" className="text-[#A7A55C]" />
                    <h4 className="font-body font-medium text-sm uppercase tracking-[0.1em] text-[#21324D]">Response Time</h4>
                  </div>
                  <p className="font-body text-base text-[#383A46]">Within 24 hours, typically sooner</p>
                </div>
                <div className="border-t border-[#383A46]/10 pt-8">
                  <p className="font-body text-sm text-[#383A46] leading-relaxed">
                    All consultations begin with a free discovery call. We'll understand your situation and recommend the right path forward. No obligations, no pressure.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
